import { sqliteTable, text, integer, index, uniqueIndex } from "drizzle-orm/sqlite-core";

// ============================================================================
// price_lists — Multi-tier pricing master (Epic #444)
//
// A price list groups item rates for a particular customer segment, channel,
// or promotional window. Examples a tenant might create:
//   - "Retail" (default for walk-in customers)
//   - "Wholesale" (assigned to B2B partners via partners.defaultPriceListId)
//   - "Q2 Promo 2026" (valid_from / valid_to bounded; auto-applies)
//
// Resolution priority lives in `getItemPrice()`:
//   explicit priceListId > customer.defaultPriceListId
//     > company.defaultPriceListId > item.defaultRate
//
// Multi-currency is OUT OF SCOPE for this iteration (per issue body) — the
// `currency` column exists for forward-compat but the resolver does not
// cross-currency convert. Default = "THB" matches `companies.defaultCurrency`.
//
// Audit trail: per issue design notes, edits to `accounting_price_list_items`
// rates DO NOT update in place. Callers MUST insert a new row with a fresh
// `valid_from` to preserve historical pricing.
// ============================================================================
export const priceLists = sqliteTable("accounting_price_lists", {
  id: text("id").primaryKey(), // ULID
  companyId: text("company_id").notNull(),
  // Display name (Thai or English) — what the user picks in the UI.
  name: text("name").notNull(),
  // ISO-4217. Currently always "THB" — see header for multi-currency note.
  currency: text("currency").notNull().default("THB"),
  // 1 = available for selection in invoice/POS; 0 = archived (historical).
  isActive: integer("is_active").notNull().default(1),
  // Optional global validity window. NULL = always valid. The resolver
  // intersects this with each price-list-item's window so a tier-priced
  // item that hasn't entered its sub-window yet still resolves through
  // the price list's parent window.
  validFrom: text("valid_from"), // YYYY-MM-DD
  validTo: text("valid_to"),     // YYYY-MM-DD inclusive
  createdAt: text("created_at").notNull(),
  createdBy: text("created_by"),
  updatedAt: text("updated_at").notNull(),
  updatedBy: text("updated_by"),
  deletedAt: text("deleted_at"),
  deletedBy: text("deleted_by"),
}, (table) => ({
  companyIdx: index("price_list_company_idx").on(table.companyId, table.isActive),
  // Per-company name uniqueness — same name across tenants is fine, two
  // active "Retail" lists in the SAME company is not.
  companyNameUnique: uniqueIndex("price_list_company_name_idx").on(table.companyId, table.name),
}));

export type PriceList = typeof priceLists.$inferSelect;
export type NewPriceList = typeof priceLists.$inferInsert;
